/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */
import * as appStrings from '@touchplan/strings';
import {
	BehaviorSubject,
	Observable,
	map,
	of,
	switchMap,
	take,
	tap,
	withLatestFrom,
	Subscription,
	catchError,
	EMPTY,
} from 'rxjs';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';

import { ImportTicketsCommonService } from './import-tickets-common.service';
import {
	ActiveLineStrategy,
	ITicketImportResponseMetadata,
	IImportVariances,
	ImportType,
	Plan,
	Role,
	Location,
	StateStep,
	SwimlanePlacement,
	Ticket,
	TicketImportErrorType,
	TicketImportFileUploadStatus,
	Variance,
	BackdropType,
	ITicketOverrides,
	TicketOverride,
	EsidDisambiguationTicketActions,
	ImportScope,
	IESIDData,
	parseESIDData,
	splitEsidData,
	joinEsidData,
	isEmptyObject,
	ScheduleTemplateFileFormat,
} from '@touchplan/common';
import { DialogPosition } from '@angular/material/dialog';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../../../common/logging/LoggerServiceFactory';
import { LoadingBlockerState } from 'src/app/common/utility-components/loading-blocker';
import {
	ExpressImportTicketsData,
	ImportTicketsData,
	ImportTicketsService,
	ImportTicketsValidationData,
	UploadImportTicketsFileResponse,
} from './import-tickets.service';
import { IExternalSystem } from 'src/app/services/external-systems.service';
import { Swimlane } from 'src/app/app-data/models/Swimlane';
import { cssVar } from '@touchplan/css-var';
import { FirebaseSnapshotsService, SnapshotInitiation } from 'src/app/services/firebase-snapshots.service';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';

interface IImportError {
	errorCode?: number;
	continueAllowed: boolean;
	errorCount?: number;
	warningCount?: number;
	errorDescription?: string;
	error?: Error;
	endpointInfo?: {};
	errorType?: TicketImportErrorType;
}
export interface IStepConfig {
	formValues: FormData;
	dialogPosition?: DialogPosition;
	dialogDimensions?: { width?: string; height?: string };
	minimalisticHeader?: boolean;
	backdropType: string;
	displayIndex?: number; //explictly specific a step index (otherwise it will match the enum)
}

export interface IUploadTicket {
	planId: string;
	importFileFormat: ScheduleTemplateFileFormat;
	importFile: File;
	importScope: ImportScope[];
	defaultRole: string | Role;
	importType: ImportType;
	swimlanePlacement?: SwimlanePlacement;
	wbsLevel?: number;
	activeLineStrategy: ActiveLineStrategy;
	dataSourceId: string;
	uploadId?: string;
}

export interface IValidateResults {
	importFile: File;
}

type MapVariancesStepResult = { variances: IImportVariances; ticketOverrides: ITicketOverrides };
type FormData = IUploadTicket | IValidateResults | MapVariancesStepResult | IESIDData;

interface IOriginalLists {
	roleList?: Observable<Map<String, Role>>;
	planList?: Observable<Map<String, Plan>>;
	locationList?: Observable<Map<String, Location>>;
	ticketList?: Observable<Map<String, Ticket>>;
	varianceList?: Observable<Map<String, Variance>>;
	swimlanesList?: Observable<Map<string, Swimlane>>;
	dataSourceList?: Observable<Map<string, IExternalSystem>>;
}

const ALLOW_CONTINUE_NEXT_STEP = true;

export interface WizardConfig {
	[step: number]: IStepConfig;
}
export interface IImportTicketsStore {
	currentStep: StateStep;
	previousStep: StateStep; // indicate if the user has jumped to an earlier step in the process, value is the previous step
	loading: LoadingBlockerState | boolean;
	projectId: string;
	planId: string;
	importFileFormat: ScheduleTemplateFileFormat;
	importFile: File;
	dataSource: string;
	defaultRole: string | Role;
	importType: ImportType;
	importScope: ImportScope[];
	swimlanePlacement?: SwimlanePlacement;
	wbsLevel?: number;
	activeLineStrategy: ActiveLineStrategy;
	esidData: IESIDData;
	variances: IImportVariances;
	ticketOverrides: ITicketOverrides;
	insertionPointY: number | null;
	lastError: IImportError;
	uploadId: string;
	prefilledEsidData: IESIDData;
	needsVariance: string[];
	importResponseMetadata: ITicketImportResponseMetadata;
	externalSystemId: string;
	isInsertionPointVisible: boolean;
	currentStepConfig: IStepConfig;
	issuesUrl?: string;
	validationImportFileChange?: boolean;
}

// PLACEHOLDER REPLACE Add/Remove/Update the resetValues as needed for each step
// the fields should be the same as the ones in the form used in each the step
const wizardConfig: WizardConfig = {
	[StateStep.INITIAL]: {
		formValues: null,
		dialogDimensions: { width: cssVar.dialogWidthXl },
		backdropType: BackdropType.COMPLETE,
	},
	[StateStep.VALIDATE]: {
		formValues: null,
		dialogDimensions: { width: cssVar.dialogWidthXl },
		backdropType: BackdropType.COMPLETE,
	},
	[StateStep.INSERTION_POINT]: {
		formValues: null,
		dialogPosition: { top: '0px' },
		dialogDimensions: { width: cssVar.dialogWidthXl },
		backdropType: BackdropType.PARTIAL,
	},
	[StateStep.ESID_DISAMBIGUATION]: {
		formValues: null,
		minimalisticHeader: true,
		dialogPosition: { top: '62px', left: '83px' },
		dialogDimensions: { width: '470px', height: 'calc(100% - 62px)' },
		backdropType: BackdropType.PARTIAL,
	},
	[StateStep.MAP_VARIANCE_REASONS]: {
		formValues: null,
		minimalisticHeader: true,
		dialogPosition: { top: '62px', left: '83px' },
		dialogDimensions: { width: '470px', height: 'calc(100% - 62px)' },
		backdropType: BackdropType.PARTIAL,
	},
	[StateStep.RESULTS]: {
		formValues: null,
		dialogDimensions: { width: cssVar.dialogWidthXl },
		backdropType: BackdropType.COMPLETE,
	},
	[StateStep.TICKET_CORRECTION]: {
		formValues: null,
		dialogDimensions: { width: cssVar.dialogWidthXl },
		backdropType: BackdropType.COMPLETE,
		displayIndex: 1,
	},
};
// END PLACEHOLDER REPLACE

export interface IImportTicketsStateAPI {
	setInsertionPointY: (observableOrValue: number | Observable<number>) => Subscription;
	insertionPointY: Observable<number>;
	isInsertionPointVisible: Observable<boolean>;
	swimlaneList: Observable<Map<string, Swimlane>>;
}

/**
 * This service efficiently manages the state of the wizard,
 * ensuring smooth progression through its various steps in the ticket import process.
 */
@Injectable()
export class ImportTicketsStateService extends ComponentStore<IImportTicketsStore> implements OnDestroy {
	private importTicketsCommonService = inject(ImportTicketsCommonService);
	private importTicketsService = inject(ImportTicketsService);
	private logger: ILoggerService = LoggerServiceFactory.create('ImportTicketStore');
	private firebaseSnapshotsService = inject(FirebaseSnapshotsService);
	private bannerMessageHandler = inject(BannerMessageHandler);

	// Add the lists of Observables to be used
	private originalLists$ = new BehaviorSubject<IOriginalLists>({});

	// Selectors
	public readonly currentStep$ = this.select((state) => state.currentStep);
	public readonly previousStep$ = this.select((state) => state.previousStep);
	public readonly loading$ = this.select((state) => state.loading);
	public readonly lastError$ = this.select((state) => state.lastError);
	public readonly insertionPointY$ = this.select((state) => state.insertionPointY);
	public readonly isInsertionPointVisible$ = this.select((state) => state.isInsertionPointVisible);
	public readonly uploadId$ = this.select((state) => state.uploadId);
	public readonly importFile$ = this.select((state) => state.importFile);
	public readonly currentStepConfig$ = this.select((state) => state.currentStepConfig);
	public readonly needsVariance$ = this.select((state) => state.needsVariance);
	public readonly esidData$ = this.select((state) => state.esidData);
	public readonly issuesUrl$ = this.select((state) => state.issuesUrl);
	public readonly projectId$ = this.select((state) => state.projectId);
	public readonly planId$ = this.select((state) => state.planId);
	/** corresponds to the plan data "on screen" */
	public readonly selectedPlan$ = this.select(this.planId$, this.planList, (planId, planList) => {
		if (!planList || !planId) {
			return null;
		}
		return planList.get(planId) || null;
	});

	// Getters
	private static get initialState(): IImportTicketsStore {
		return {
			currentStep: StateStep.INITIAL,
			previousStep: null,
			loading: false,
			projectId: null,
			planId: null,
			importFileFormat: null,
			importFile: null,
			dataSource: null,
			defaultRole: null,
			importType: null,
			importScope: [],
			swimlanePlacement: null,
			wbsLevel: 0,
			activeLineStrategy: null,
			esidData: null,
			variances: null,
			ticketOverrides: null,
			insertionPointY: null,
			lastError: null,
			uploadId: null,
			prefilledEsidData: null,
			needsVariance: [],
			importResponseMetadata: null,
			externalSystemId: null,
			isInsertionPointVisible: false,
			currentStepConfig: wizardConfig[StateStep.INITIAL],
			validationImportFileChange: false,
		};
	}

	get roleList(): Observable<Map<string, Role>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) => (originalLists.roleList ? originalLists.roleList : of(new Map())))
		);
	}

	get locationList(): Observable<Map<string, Location>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) =>
				originalLists.locationList
					? (originalLists.locationList as Observable<Map<string, Location>>)
					: of(new Map())
			)
		);
	}

	get planList(): Observable<Map<string, Plan>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) =>
				originalLists.planList ? (originalLists.planList as Observable<Map<string, Plan>>) : of(new Map())
			)
		);
	}

	get ticketList(): Observable<Map<string, Ticket>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) => (originalLists.ticketList ? originalLists.ticketList : of(new Map())))
		);
	}

	get varianceList(): Observable<Map<string, Variance>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) => (originalLists.varianceList ? originalLists.varianceList : of(new Map())))
		);
	}

	get dataSourceList(): Observable<Map<string, IExternalSystem>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) =>
				originalLists.dataSourceList ? originalLists.dataSourceList : of(new Map())
			)
		);
	}

	get swimlanesList(): Observable<Map<string, Swimlane>> {
		return this.originalLists$.pipe(
			switchMap((originalLists) =>
				originalLists.swimlanesList ? originalLists.swimlanesList : of(new Map())
			)
		);
	}

	get validationImportFileChange(): Observable<boolean> {
		return this.select((state) => state.validationImportFileChange);
	}

	// Updaters
	private setCurrentStep = this.updater((state, currentStep: StateStep) => {
		return {
			...state,
			previousStep: Math.max(state.currentStep, state.previousStep),
			currentStep,
			currentStepConfig: wizardConfig[currentStep],
		};
	});

	private setLoading = this.updater((state, loading: LoadingBlockerState | boolean) => {
		return { ...state, loading };
	});

	private setError = this.updater((state, lastError: IImportError) => {
		return {
			...state,
			lastError,
			loading: false,
		};
	});

	private setInsertionPointY = this.updater((state, insertionPointY: number) => {
		return { ...state, insertionPointY };
	});

	private setInsertionPointVisibility = this.updater((state, isInsertionPointVisible: boolean) => {
		return { ...state, isInsertionPointVisible };
	});

	private setUploadId = this.updater((state, uploadId: string) => {
		return { ...state, uploadId };
	});

	private setTicketImportFileUploadStatus = this.updater(
		(state, ticketImportFileUploadStatus: TicketImportFileUploadStatus) => {
			return { ...state, ticketImportFileUploadStatus };
		}
	);

	private incrementStep = this.updater((state) => ({
		...state,
		loading: false,
		previousStep: state.currentStep,
		currentStep: state.currentStep + 1,
		currentStepConfig: wizardConfig[state.currentStep + 1],
	}));

	private cleanError = this.updater((state) => ({
		...state,
		lastError: ImportTicketsStateService.initialState.lastError,
	}));

	private patchOriginalLists(newList: IOriginalLists) {
		this.originalLists$.next({ ...this.originalLists$.value, ...newList });
	}

	private updateState = this.updater((state, fields: Partial<IImportTicketsStore>) => ({
		...state,
		...fields,
	}));

	private updateError = this.updater((state, lastError: IImportError) => ({
		...state,
		lastError,
	}));

	private updateValidationImportFileChange = this.updater((state, validationImportFileChange: boolean) => ({
		...state,
		validationImportFileChange,
	}));

	public setProjectPlan = this.updater((state, data: { projectId: string; planId: string }) => {
		return { ...state, ...data };
	});

	/**
	 * Next Step
	 * This method is called by the wizard component when the user clicks the "Next" button.
	 * It is responsible for processing the data from the current step and moving to the next step.
	 * @param formData - The data from the current step.
	 **/
	public nextStep = this.effect<FormData>((stepData$) => {
		return stepData$.pipe(
			switchMap((stepData) => {
				this.loading(true);
				return this.processStepData(stepData, this.get().currentStep + 1);
			}),
			tapResponse(
				(continueToNextStep) => {
					if (continueToNextStep) {
						this.incrementStep();
					} else {
						this.setLoading(false);
					}
				},
				(error: Error) => {
					this.setError({
						errorDescription: error.message,
						error,
					} as IImportError);
					this.logger.log(error);
				}
			)
		);
	});

	// Jump backwards arbitrarily
	public goToStep = this.effect<StateStep>((step$) => {
		return step$.pipe(
			withLatestFrom(this.currentStep$),
			tapResponse<[StateStep, StateStep]>(
				([goToStep, currentStep]) => {
					// Throw an error if the user tries to go forward in time
					if (goToStep > currentStep) {
						// This does not modify the current step
						// This error will be caught by the error handler,
						// so it will be treated as an import error. Should be modified if state errors are just for import errors.
						throw new Error('Cannot go forward in time');
					}

					this.setCurrentStep(goToStep);
				},
				(error: Error) => {
					this.setError({
						continueAllowed: false,
						errorDescription: error.message,
						error,
						errorType: TicketImportErrorType.UNRECOVERABLE,
					});
					this.logger.log(error);
				}
			)
		);
	});

	// Loading
	public loading = this.effect<LoadingBlockerState | boolean>((loading$) => {
		return loading$.pipe(
			tapResponse<LoadingBlockerState | boolean>(
				(loadingState) => {
					this.setLoading(loadingState);
				},
				(error) => {
					this.logger.log(error);
				}
			)
		);
	});

	// Previous Step
	public previousStep() {
		this.goToStep(this.get().currentStep - 1);
	}

	// Update Role List
	public updateRoleLists(roleList: Observable<Map<string, Role>>) {
		if (this.originalLists$.value.roleList !== roleList) {
			this.patchOriginalLists({ roleList });
		}
	}

	// Update Location List
	public updateLocationLists(locationList: Observable<Map<string, Location>>) {
		if (this.originalLists$.value.locationList !== locationList) {
			this.originalLists$['locationList'] = locationList;
			this.patchOriginalLists({ locationList });
		}
	}

	public updatePlanLists(planList: Observable<Map<string, Plan>>) {
		if (this.originalLists$.value.planList !== planList) {
			this.patchOriginalLists({ planList });
		}
	}

	public updateTicketList(ticketList: Observable<Map<string, Ticket>>) {
		if (this.originalLists$.value.ticketList !== ticketList) {
			this.patchOriginalLists({ ticketList });
		}
	}

	public updateVarianceList(varianceList: Observable<Map<string, Variance>>) {
		if (this.originalLists$.value.varianceList !== varianceList) {
			this.patchOriginalLists({ varianceList });
		}
	}

	public updateDataSourcesList(dataSourceList: Observable<Map<string, IExternalSystem>>) {
		if (!!dataSourceList && this.originalLists$.value.dataSourceList !== dataSourceList) {
			this.patchOriginalLists({ dataSourceList });
		}
	}

	public updateSwimlanesList(swimlanesList: Observable<Map<string, Swimlane>>) {
		if (!!swimlanesList && this.originalLists$.value.swimlanesList !== swimlanesList) {
			this.patchOriginalLists({ swimlanesList });
		}
	}

	// Update All Lists at Once
	public updateAllLists(lists: IOriginalLists) {
		this.patchOriginalLists(lists);
	}

	public updateValidationFileChange(validationImportFileChange: boolean) {
		this.updateValidationImportFileChange(validationImportFileChange);
	}

	public registerDialogStateAPI(dialogId: string): void {
		// Automatically subscribe to the destroy$ event to unregister the service once it is destroyed.
		this.importTicketsCommonService.registerImportTicketsDialogStateAPI(dialogId, {
			setInsertionPointY: this.setInsertionPointY,
			insertionPointY: this.insertionPointY$,
			isInsertionPointVisible: this.isInsertionPointVisible$,
			swimlaneList: this.swimlanesList,
		});
		this.destroy$.subscribe(() => {
			this.importTicketsCommonService.unRegisterImportTicketsDialogStateAPI(dialogId);
			this.originalLists$.complete();
			this.originalLists$.unsubscribe();
		});
	}

	/**
	 * Process Step Data
	 * This method is responsible for processing the step data.
	 * targetStep is optional and is used to indicate if the user has jumped to an earlier step in the process.
	 * @param formData - The data from the current step.
	 * @param targetStep - The step the user has jumped to.
	 * @returns An Observable emitting a boolean indicating if the user can continue or should stay in the current step.
	 **/
	private processStepData(formData: FormData, targetStep?: StateStep): Observable<boolean> {
		const step = this.get().currentStep;
		switch (step) {
			case StateStep.INITIAL:
				return this.processInitialStep(formData as IUploadTicket, targetStep);
			case StateStep.VALIDATE:
				return this.processValidateStep(formData as IValidateResults, targetStep);
			case StateStep.TICKET_CORRECTION:
				return this.processCorrectionStep(formData, targetStep);
			case StateStep.INSERTION_POINT:
				return this.processInsertionPointStep(targetStep);
			case StateStep.ESID_DISAMBIGUATION:
				return this.processEsidDisambiguation(formData as IESIDData, targetStep);
			case StateStep.MAP_VARIANCE_REASONS:
				return this.processMapVarianceReasonsStep(formData as MapVariancesStepResult);
			default:
				return of(false);
		}
	}

	// Steps Process Methods and Validators
	private processInitialStep(formData: IUploadTicket, targetStep?: StateStep): Observable<boolean> {
		const currentStep = this.get().currentStep;

		// From INITIAL step to immediate next step processing
		if (currentStep === StateStep.INITIAL && (!targetStep || targetStep === currentStep + 1)) {
			this.updateState({
				uploadId: formData.uploadId, // only for boardscan flow
				planId: formData.planId,
				importFileFormat: formData.importFileFormat,
				importFile: formData.importFile,
				defaultRole: formData.defaultRole,
				importType: formData.importType,
				swimlanePlacement: formData.swimlanePlacement,
				wbsLevel: formData.wbsLevel,
				activeLineStrategy: formData.activeLineStrategy,
				externalSystemId: formData.dataSourceId,
				importScope: formData.importScope,
			});

			//Boardscan: shortcircuit to step 3
			if (this.get().importFileFormat === ScheduleTemplateFileFormat.Boardscan) {
				// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7046
				// swap for mocks
				// this.setCurrentStep(StateStep.TICKET_CORRECTION);
				// return of(false);
				return this.ingestBoardscanTickets(this.get().projectId, this.get().uploadId).pipe(
					map((result) => {
						this.logger.log('ingestBoardscanTickets result', result);
						this.setCurrentStep(StateStep.TICKET_CORRECTION);
						return false;
					})
				);
				// END PLACEHOLDER REPLACE AS PART OF TP-7046
			}

			return this.uploadAndValidate().pipe(
				map(() => {
					if (
						!this.get().lastError.errorCount &&
						!this.get().lastError.warningCount &&
						this.get().lastError.errorType !== TicketImportErrorType.UNRECOVERABLE
					) {
						this.setInsertionPointVisibility(true);
						this.setCurrentStep(StateStep.INSERTION_POINT);
						return false;
					}
					return ALLOW_CONTINUE_NEXT_STEP;
				})
			);
		}
	}

	private processValidateStep(formData: IValidateResults, targetStep?: StateStep): Observable<boolean> {
		const currentStep = this.get().currentStep;
		// From VALIDATE step to immediate next step processing
		if (currentStep === StateStep.VALIDATE && (!targetStep || targetStep === currentStep + 1)) {
			if (this.get().lastError.continueAllowed && !this.get().validationImportFileChange) {
				// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5850
				this.setInsertionPointVisibility(true);
				// END PLACEHOLDER REPLACE AS PART OF TP-5850
				return of(ALLOW_CONTINUE_NEXT_STEP);
			}

			//...and now actually put the new file into state because uploadAndValidate depends on it
			this.updateState({
				validationImportFileChange: false,
				importFile: formData.importFile,
			});

			return this.uploadAndValidate().pipe(
				map(() => {
					return !ALLOW_CONTINUE_NEXT_STEP;
				})
			);
		}
	}
	//BEGIN PLACEHOLDER REPLACE AS PART OF TP-7062
	private processCorrectionStep(formData: any, targetStep?: StateStep): Observable<boolean> {
		const currentStep = this.get().currentStep;
		// From VALIDATE step to immediate next step processing
		// why did validate even need this check? (copied)
		if (currentStep === StateStep.TICKET_CORRECTION && (!targetStep || targetStep === currentStep + 1)) {
			return of(false).pipe(
				//no like, current things are doing this in a side effect of the return value
				// and it appears to _require_ it /sigh. Maybe hide a fix for this in the big ticket
				tap(() => {
					// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5850
					this.setInsertionPointVisibility(true);
					// END PLACEHOLDER REPLACE AS PART OF TP-5850
					this.setCurrentStep(StateStep.INSERTION_POINT);
				})
			);
			// }

			//...and now actually put the new file into state because uploadAndValidate depends on it
			// this.updateState({
			// 	validationImportFileChange: false,
			// 	importFile: formData.importFile,
			// });
		}
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7062

	private processInsertionPointStep(targetStep?: StateStep): Observable<boolean> {
		const currentStep = this.get().currentStep;
		// From INSERTION_POINT step to immediate next step processing
		if (currentStep === StateStep.INSERTION_POINT && (!targetStep || targetStep === currentStep + 1)) {
			return this.insertionPointY$.pipe(
				map((insertionPointY) => {
					if (insertionPointY !== null && insertionPointY !== undefined) {
						this.setInsertionPointVisibility(false);
						return ALLOW_CONTINUE_NEXT_STEP;
					} else {
						throw new Error(appStrings.IMPORT_TICKET_INSERTION_POINT_MISSING_MSG);
					}
				}),
				switchMap(() => {
					//if data source is boardscan, import express
					if (this.get().importFileFormat === ScheduleTemplateFileFormat.Boardscan) {
						return this.firebaseSnapshotsService
							.takeSnapshot$(this.get().projectId, this.get().planId, {
								initiatedBy: SnapshotInitiation.SystemOnBulkUpdate,
							})
							.pipe(
								// We need to map to true so it proceeds to the next step
								// if the snapshot is taken successfully; otherwise, catchError
								// will map it to false and it won't proceed to the next step.
								map(() => true),
								catchError((error) => {
									this.logger.log('Error taking snapshot before import', error);
									this.bannerMessageHandler.displayErrorMessage(error, appStrings.IMPORT_ERROR_MESSAGE);
									return of(false);
								}),
								switchMap((allowedToContinue) => {
									// Basically, what we want is that when allowedToContinue is false,
									// the nextStep function receives false and does not continue to the next step,
									// removing the loading state and allowing the user to try again.
									if (!allowedToContinue) {
										return of(false);
									}
									return this.importExpress(this.get().projectId, this.get().uploadId).pipe(
										map((response) => {
											this.setCurrentStep(StateStep.RESULTS);
											return false;
										})
									);
								})
							);
					}

					if (targetStep === StateStep.ESID_DISAMBIGUATION && !this.get().esidData) {
						if (targetStep + 1 === StateStep.MAP_VARIANCE_REASONS && !this.get().needsVariance?.length) {
							return this.finishImport().pipe(
								map(() => {
									this.setCurrentStep(StateStep.RESULTS);
									return false;
								})
							);
						}
						this.setCurrentStep(StateStep.MAP_VARIANCE_REASONS);
						return of(false);
					}
					return of(ALLOW_CONTINUE_NEXT_STEP);
				})
			);
		}
	}

	private processEsidDisambiguation(esidData: IESIDData, targetStep?: StateStep): Observable<boolean> {
		const ignoredTickets: string[] = [];
		const currentNeedsVariance = this.get().needsVariance;
		if (esidData) {
			// Iterate through the esidData
			for (const key of Object.keys(esidData)) {
				const tickets = esidData[key].tickets;
				if (tickets) {
					// Iterate through the tickets
					for (const [ticketId, disambiguation] of Object.entries(tickets)) {
						if (disambiguation.action === EsidDisambiguationTicketActions.Ignore) {
							ignoredTickets.push(ticketId);
						}
					}
				}
			}
		}

		const prefilledEsidData = this.get().prefilledEsidData;
		if (prefilledEsidData) {
			// Iterate through the prefilledEsidData
			for (const key of Object.keys(prefilledEsidData)) {
				const tickets = prefilledEsidData[key].tickets;
				if (tickets) {
					// Iterate through the tickets
					for (const [ticketId, disambiguation] of Object.entries(tickets)) {
						if (disambiguation.action === EsidDisambiguationTicketActions.Ignore) {
							ignoredTickets.push(ticketId);
						}
					}
				}
			}
		}

		const needsVariance = currentNeedsVariance.filter((ticketId) => !ignoredTickets.includes(ticketId));
		this.updateState({ esidData, needsVariance });

		if (targetStep === StateStep.MAP_VARIANCE_REASONS && !this.get().needsVariance?.length) {
			return this.finishImport().pipe(
				map(() => {
					this.setCurrentStep(StateStep.RESULTS);
					return false;
				})
			);
		}
		return of(ALLOW_CONTINUE_NEXT_STEP);
	}

	private processMapVarianceReasonsStep({
		variances,
		ticketOverrides,
	}: MapVariancesStepResult): Observable<boolean> {
		let isStepValid = true;
		if (variances) {
			for (const [_, variance] of Object.entries(variances)) {
				if (!variance.id) {
					isStepValid = false;
					break;
				}
			}
		}

		if (ticketOverrides) {
			for (const [_, ticketOverride] of Object.entries(ticketOverrides)) {
				if (ticketOverride[0] !== TicketOverride.CUT_DATES) {
					isStepValid = false;
					break;
				}
			}
		}

		if (isStepValid) {
			this.updateState({ variances, ticketOverrides });
			return this.finishImport().pipe(
				map(() => {
					return ALLOW_CONTINUE_NEXT_STEP;
				})
			);
		} else {
			return of(false);
		}
	}

	private uploadAndValidate(): Observable<{ lastError: IImportError }> {
		return this.uploadImportTicketFile$(this.get().importFile).pipe(
			switchMap((response) => {
				this.setUploadId(response.uploadId);
				return this.validateImportTicketFile$();
			}),
			tapResponse(
				(validationResults) => {
					this.updateState(validationResults);
				},
				(error: Error) => {
					this.setError({
						continueAllowed: false,
						errorDescription: error.message,
						error,
						errorType: TicketImportErrorType.UNRECOVERABLE,
					});
					this.logger.log(error);
				}
			)
		);
	}

	private ingestBoardscanTickets(projectId: string, jobId: string) {
		return this.importTicketsService.processBSTickets(projectId, jobId).pipe(
			catchError((error) => {
				this.logger.log('process tickets error', error);

				//ugh, there's like... 3 different methods of api error handling in this file
				// TODO - achieve sanity
				this.setError({
					continueAllowed: false,
					errorDescription: error.error?.errorDescription,
					error,
					errorType: TicketImportErrorType.UNRECOVERABLE,
				});
				return EMPTY;
			})
		);
	}

	private importExpress(projectId: string, jobId: string) {
		// TODO - most of this is unncessary, but it will be null/ undefined
		const requestData: ExpressImportTicketsData = {
			projectId,
			jobId,
			planId: this.get().planId,
			importType: this.get().importType,
			insertionPointY: this.get().insertionPointY === null ? null : Math.round(this.get().insertionPointY),
			externalSystemId: this.get().externalSystemId,
			swimlanePlacement: this.get().swimlanePlacement,
			wbsLevel: this.get().wbsLevel,
		};

		return this.importTicketsService.expressImport(requestData).pipe(
			catchError((e) => {
				this.logger.error('importExpress error', e);
				//TODO (this was copied, so import has the same problem), handling error and success in the same stream
				// is kinda... off
				return of({ errorDescription: e.error.errorDescription });
			}),
			tap((importResponseData) => this.saveImportResponseData(importResponseData))
		);
	}

	private uploadImportTicketFile$(file: File): Observable<UploadImportTicketsFileResponse> {
		return this.importTicketsService.uploadImportTicketsFile(file).pipe(
			take(1),
			tap(() => {
				this.setTicketImportFileUploadStatus(TicketImportFileUploadStatus.Success);
			})
		);
	}

	private validateImportTicketFile$(): Observable<{ lastError: IImportError }> {
		const requestData: ImportTicketsValidationData = {
			uploadId: this.get().uploadId,
			projectId: this.get().projectId,
			planId: this.get().planId,
			defaultRoleId: this.get().defaultRole,
			externalSystemId: this.get().externalSystemId,
			swimlanePlacement: this.get().swimlanePlacement,
			wbsLevel: this.get().wbsLevel,
			importFileFormat: this.get().importFileFormat,
			importScope: this.get().importScope,
			importType: this.get().importType,
		};

		return this.importTicketsService.validateImport(requestData).pipe(
			map((response) => {
				let esidData = !this.get().importType ? null : response.esidData;
				const parsedEsidData =
					esidData && !isEmptyObject(esidData) ? parseESIDData(this.get().importType, esidData) : null;

				let prefilledEsidData = null;

				if (parsedEsidData) {
					const { withUnset, withoutUnset } = splitEsidData(parsedEsidData);
					esidData = !isEmptyObject(withUnset) ? withUnset : null;
					prefilledEsidData = !isEmptyObject(withoutUnset) ? withoutUnset : null;
				}
				return {
					esidData,
					prefilledEsidData,
					needsVariance: response.needsVariance ?? this.get().needsVariance,
					issuesUrl: response.issuesUrl,
					lastError: {
						continueAllowed: response.errorCount === 0,
						errorCount: response.errorCount || 0,
						warningCount: response.warningCount || 0,
						errorType: TicketImportErrorType.RECOVERABLE,
					},
				};
			}),
			catchError((error) => {
				return of({
					lastError: {
						error,
						continueAllowed: false,
						errorDescription: error.error?.errorDescription,
						errorType: TicketImportErrorType.UNRECOVERABLE,
						errorCode: error.status,
					},
				});
			})
		);
	}

	private finishImport(): Observable<ITicketImportResponseMetadata> {
		const requestData: ImportTicketsData = {
			esidData: joinEsidData(this.get().esidData, this.get().prefilledEsidData),
			variances: this.get().variances,
			ticketOverrides: this.get().ticketOverrides,
			uploadId: this.get().uploadId,
			importType: this.get().importType,
			importScope: this.get().importScope,
			activeLineStrategy: this.get().activeLineStrategy,
			insertionPointY: this.get().insertionPointY === null ? null : Math.round(this.get().insertionPointY),
			externalSystemId: this.get().externalSystemId,
			swimlanePlacement: this.get().swimlanePlacement,
			wbsLevel: this.get().wbsLevel,
		};

		return this.importTicketsService.import(requestData).pipe(
			catchError((e) => {
				console.error(e);
				return of({ errorDescription: e.error.errorDescription });
			}),
			tap((importResponseData) => this.saveImportResponseData(importResponseData))
		);
	}

	private saveImportResponseData(responseData: ITicketImportResponseMetadata) {
		this.updateState({
			importResponseMetadata: responseData,
		});
	}

	constructor() {
		super(ImportTicketsStateService.initialState);
	}
}
